import {Children,useEffect,useId,useRef,useState} from 'react';
import {installStudioDragScroll} from './studio-scroll.js';
import './studio-workspace.css';

const CLOSED=78;
const clamp=(value,min,max)=>Math.max(min,Math.min(max,value));

export default function StudioWorkspace({children}) {
  const [stage,tools]=Children.toArray(children);
  const root=useRef(null),tray=useRef(null),drag=useRef(null),lastExpanded=useRef(null),ignoreClick=useRef(false);
  const [height,setHeight]=useState(null),[available,setAvailable]=useState(0);
  const panelId=useId();
  const maxHeight=Math.max(180,available-200),minExpanded=Math.min(205,maxHeight);
  const collapsed=height!==null&&height<=CLOSED;
  const panelHeight=drag.current?.moved?clamp(height,CLOSED,maxHeight):collapsed?CLOSED:clamp(height??available*.48,minExpanded,maxHeight);
  useEffect(()=>{
    const observer=new ResizeObserver(()=>setAvailable(root.current?.clientHeight||0));
    observer.observe(root.current);setAvailable(root.current.clientHeight);
    return()=>observer.disconnect();
  },[]);
  useEffect(()=>installStudioDragScroll(tray.current),[]);
  const expand=()=>setHeight(clamp(lastExpanded.current??available*.48,minExpanded,maxHeight));
  const toggle=()=>{
    if(collapsed)expand();
    else {lastExpanded.current=panelHeight;setHeight(CLOSED);}
  };
  const finish=e=>{
    if(!drag.current||drag.current.id!==e.pointerId)return;
    const current=drag.current;drag.current=null;
    if(current.moved){
      ignoreClick.current=true;
      if(current.height<(CLOSED+minExpanded)/2)setHeight(CLOSED);
      else {const next=clamp(current.height,minExpanded,maxHeight);lastExpanded.current=next;setHeight(next);}
    }else if(e.type==='pointerup'&&e.pointerType!=='mouse'){
      // Some mobile browsers omit/delay click after pointer capture; respond on release.
      ignoreClick.current=true;toggle();
    }
  };
  return <div ref={root} className="workspace-v2 adjustable-workspace" style={available?{'--studio-tray-height':`${panelHeight}px`}:undefined}>
    {stage}
    <section ref={tray} className={`studio-tool-tray${collapsed?' is-collapsed':''}`} onClickCapture={e=>{
      if(collapsed&&e.target.closest('.st-tabs button,.st-advanced-icon'))expand();
    }}>
      <button type="button" className="studio-tray-handle" aria-label={collapsed?'展开工具面板':'收起工具面板'} aria-expanded={!collapsed} aria-controls={panelId}
        title="上下拖动调整面板，点击收起或展开"
        onClick={e=>{const suppress=ignoreClick.current&&e.detail!==0;ignoreClick.current=false;if(!suppress)toggle();}}
        onPointerDown={e=>{
          if(!e.isPrimary||e.button!==0)return;
          ignoreClick.current=false;drag.current={id:e.pointerId,y:e.clientY,height:panelHeight,start:panelHeight,moved:false};
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={e=>{
          const current=drag.current;if(!current||current.id!==e.pointerId)return;
          const dy=e.clientY-current.y;if(!current.moved&&Math.abs(dy)<5)return;
          current.moved=true;current.height=clamp(current.start-dy,CLOSED,maxHeight);
          setHeight(current.height);
        }} onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish}
        onKeyDown={e=>{
          if(!['ArrowUp','ArrowDown','Home','End'].includes(e.key))return;
          e.preventDefault();
          if(e.key==='Home'){if(!collapsed)lastExpanded.current=panelHeight;setHeight(CLOSED);}
          else if(e.key==='End')setHeight(maxHeight);
          else if(e.key==='ArrowUp')setHeight(clamp(panelHeight+48,minExpanded,maxHeight));
          else {const next=panelHeight-48;if(next<minExpanded){lastExpanded.current=panelHeight;setHeight(CLOSED);}else setHeight(next);}
        }}><span aria-hidden="true"/><i aria-hidden="true">{collapsed?'⌃':'⌄'}</i></button>
      <div id={panelId} className="studio-tray-body">{tools}</div>
    </section>
  </div>;
}
